// components/Charts/RevenueLineChart.jsx
import React, { useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Area, ComposedChart
} from "recharts";
import { motion } from "framer-motion";

// Mock data for different time periods
const dataPeriods = {
  "7D": [
    { date: "Jul 1", revenue: 2400 },
    { date: "Jul 2", revenue: 3000 },
    { date: "Jul 3", revenue: 2800 },
    { date: "Jul 4", revenue: 3500 },
    { date: "Jul 5", revenue: 4000 },
    { date: "Jul 6", revenue: 4200 },
    { date: "Jul 7", revenue: 4600 },
  ],
  "30D": Array.from({ length: 30 }, (_, i) => ({
    date: `Jul ${i + 1}`,
    revenue: 2000 + Math.round(Math.random() * 3000),
  })),
  "90D": Array.from({ length: 90 }, (_, i) => ({
    date: `${Math.floor(i / 30) + 1}/${(i % 30) + 1}`,
    revenue: 1800 + Math.round(Math.random() * 4000),
  })),
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-3 rounded-lg shadow-lg border border-gray-200"
      >
        <p className="font-semibold text-gray-800">{label}</p>
        <p className="text-indigo-600">
          ${payload[0].value.toLocaleString()}
        </p>
      </motion.div>
    );
  }
  return null;
};

const RevenueLineChart = () => {
  const [timePeriod, setTimePeriod] = useState("7D");
  const [data, setData] = useState(dataPeriods["7D"]);

  const handleTimePeriodChange = (period) => {
    setTimePeriod(period);
    setData(dataPeriods[period]);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white p-6 rounded-xl shadow-md"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-800">Revenue Trend</h2>
        <div className="flex space-x-2">
          {["7D", "30D", "90D"].map((period) => (
            <motion.button
              key={period}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                timePeriod === period
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => handleTimePeriodChange(period)}
            >
              {period}
            </motion.button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart
          data={data}
          margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
        >
          <defs>
            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6366F1" stopOpacity={0.4} />
              <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="#e5e7eb" 
            vertical={false} 
          />
          <XAxis 
            dataKey="date" 
            tick={{ fill: '#9CA3AF', fontSize: 12 }}
            tickMargin={10}
          />
          <YAxis 
            tick={{ fill: '#9CA3AF', fontSize: 12 }}
            tickFormatter={(value) => `$${value / 1000}k`}
            tickMargin={10}
          />
          <Tooltip 
            content={<CustomTooltip />} 
            cursor={{ stroke: '#e5e7eb', strokeWidth: 1 }}
          />
          <Area
            type="monotone"
            dataKey="revenue"
            stroke="#6366F1"
            fill="url(#colorRevenue)"
            fillOpacity={1}
            strokeWidth={3}
            activeDot={{ r: 6, stroke: '#6366F1', strokeWidth: 2, fill: 'white' }}
            animationDuration={1000}
            animationEasing="ease-out"
          />
          <Line
            type="monotone"
            dataKey="revenue"
            stroke="#6366F1"
            strokeWidth={3}
            dot={{ r: 4, stroke: '#6366F1', strokeWidth: 2, fill: 'white' }}
            activeDot={{ r: 6, stroke: '#6366F1', strokeWidth: 2, fill: 'white' }}
            animationDuration={1000}
            animationEasing="ease-out"
          />
        </ComposedChart>
      </ResponsiveContainer>

      <div className="mt-4 flex justify-end">
        <p className="text-xs text-gray-500">
          Updated: {new Date().toLocaleDateString()}
        </p>
      </div>
    </motion.div>
  );
};

export default RevenueLineChart;